import Papa from 'papaparse';
import moment from 'moment';
import getDate from '../../../common/get-date';
import type { CoreTypes } from '../../../core';
import type { Data, Options, Warning } from '../types';
import { generateGUID, resolveId } from './util';
import { fromWeekNumberRange, parsePeriod } from './period';
import { parseCalendarExceptions } from './calendar-exceptions';
import { extractStartAndEnd } from './division';


const daysToIndex = {
  'Måndag':  0,
  'Tisdag':  1,
  'Onsdag':  2,
  'Torsdag': 3,
  'Fredag':  4,
  'Lördag':  5,
  'Söndag':  6,

  // and if ÅÄÖ is loaded correctly
  'M�ndag': 0,
  'L�rdag': 5,
  'S�ndag': 6,
} as Record<string, number | undefined>;


function transformHeader (header: string) {
  return header.split(/[^a-zA-Z]+/g)[0];
}
const parseConfig = {
  quotes:         false, //or array of booleans
  quoteChar:      '"',
  escapeChar:     '"',
  delimiter:      '\t',
  header:         true,
  // newline: "\r\n",
  skipEmptyLines: false, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
} as Papa.ParseConfig;


function extractTables (input: string, warnings: Warning[]) {
  // split at [Tables] to get meta section and table section
  const split = input.split('[Tables]');
  const tables = split.at(1);
  if (!tables) throw new Error('Unable to extract tables from input');

  ////
  ////  Capture the TABLE NAME and the TABLE CONTENT using regex
  ////
  ////  TABLE NAME
  ////  [Rows]
  ////  TABLE CONTENT
  ////
  const regex = /([^\r\n]+)(?:\r\n|\r|\n)\[Rows\](?:\r\n|\r|\n)((?:[^\r\n].+(?:(?:\r\n|\r|\n)|$))*)/gm;
  const matches = [...tables.trim().matchAll(regex)];

  return matches
    .map(x => {
      // extract only the true table name "<table name> (<number>)", i.e., ignore the brackets and the preceding space
      const tableName = x.at(1)
        ?.trim()
        .match(/(.*?)(?:\s*\(\d+\))$/m)
        ?.at(1);
      if (!tableName) return;

      const tableContent = x.at(2)?.trim();
      if (!tableContent) return;
      const parsed = Papa.parse(tableContent, { ...parseConfig, transformHeader });
      return {
        tableName: tableName as keyof Data,
        parsed:    parsed.data
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null)
    .reduce<Data>((acc, { tableName, parsed }) => {
      acc[tableName] = parsed;
      return acc;
    }, {} as Data);
}


function parseColor (colorValue: string | undefined) {
  if (colorValue == null) return;

  const integerValue = parseInt(colorValue);
  if (isNaN(integerValue)) return;

  // convert to decimal string
  const hex = integerValue.toString(16);

  // ensure its a valid color
  if (!/^(?:[0-9a-fA-F]{3}){1,2}$/.test(hex)) return;
  return `#${ hex }`;
}


export namespace OutTypes {
  export type settings = CoreTypes.SerializedWithOptionalId.Settings;
  export type teacher  = CoreTypes.SerializedWithOptionalId.Teacher;
  export type person   = CoreTypes.SerializedWithOptionalId.Person;
  export type group    = CoreTypes.SerializedWithOptionalId.Group;
  export type course   = CoreTypes.SerializedWithOptionalId.Course;
  export type location = CoreTypes.SerializedWithOptionalId.Location;
  export type event    = CoreTypes.SerializedWithOptionalId.Event;
  export type period   = CoreTypes.SerializedWithOptionalId.Period;
}

type Out = {
  settings:  OutTypes.settings;
  periods:   OutTypes.period  [];
  teachers:  OutTypes.teacher [];
  persons:   OutTypes.person  [];
  groups:    OutTypes.group   [];
  locations: OutTypes.location[];
  courses:   OutTypes.course  [];
  events:    OutTypes.event   [];
  meta?: {
    warnings: Warning[]
  };
};


export const schedules = (
  input:   string,
  options: Options = {}
): Out => {
  const warnings: Warning[] = [];

  const tables = extractTables(input, warnings);

  const _scheduleRange = extractStartAndEnd(input) ?? options.fallbackScheduleRange;   // need whole input file
  if (!_scheduleRange) throw new Error('Unable to extract schedule range');

  const scheduleStart = moment.utc(_scheduleRange.start);
  const startYear     = scheduleStart.year();

  const settings: OutTypes.settings = {
    discretization:     5,
    numDays:            5,
    dayStart:           '8:00',
    potentialCenter:    '12:00',
    dayEnd:             '17:00',
    calendarExceptions: parseCalendarExceptions(tables.PeriodGeneral?.at(0), startYear, warnings)
  };

  const resolveGuid = (guid: Parameters<typeof resolveId>[0]) => resolveId(guid, { generateIfMissing: !!options.allowGeneratedGUIDs });


  // parse teachers
  const [teacherPersons, teachers] = (tables.Teacher ?? [])
    .map((x) => {
      const teacherPersonId = generateGUID();

      const teacher: OutTypes.teacher = {
        ids:         resolveGuid(x.GUID),
        signature:   x.Teacher,
        displayName: `${x.FirstName} ${x.LastName}`,
        ...x.Category && {
          tags: [
            {
              type:  'category',
              value: x.Category
            }
          ]
        },
        person: teacherPersonId
      };

      const teacherPerson: OutTypes.person = {
        ids:       teacherPersonId,
        firstName: x.FirstName,
        lastName:  x.LastName,
        ...x.PersonalCode && {
          SSN: {
            value: x.PersonalCode,
          }
        },
        ...x.EMail && {
          emails: [{
            value: x.EMail,
            type:  'organization'
          }]
        },
        ...x.Phone && {
          phoneNumbers: [
            {
              value: x.Phone,
              type:  'organization'
            }
          ]
        }
      };

      return [teacherPerson, teacher] as const;
    })
    .reduce<[OutTypes.person[], OutTypes.teacher[]]>((acc, [teacherPerson, teacher]) => {
      acc[0].push(teacherPerson);
      acc[1].push(teacher);
      return acc;
    }, [[], []]);
  const teacherMap = new Map(
    teachers.map(x => x.ids && x.signature ? [x.signature, x.ids] as const : null).filter(x => x != null)
  );


  // parse students
  const students = tables.Student?.map((x): OutTypes.person => ({
    ids:       resolveGuid(x.GUID),
    firstName: x.FirstName,
    lastName:  x.LastName,
    type:      'Student',
    ...x.Student && {
      SSN: {
        value: x.Student,
      }
    },
    ...x.EMail && {
      emails: [{
        value: x.EMail,
        type:  'organization'
      }]
    }
  }));
  const studentMap = new Map((students ?? [])
    .map(x => x.ids && x.SSN?.value ? [x.SSN.value, x.ids] as const : null)
    .filter(x => x != null)
  );


  // parse groups
  const groups = tables.Group?.map((x): OutTypes.group => ({
    ids:         resolveGuid(x.GUID),
    displayName: x.Group,
    ...(x.Class == '1') && { species: 'class' },
    members:     x.Student?.split(',').map(x => studentMap.get(x)).filter(x => x != null) ?? []
  }));
  const groupMap = new Map(
    (groups ?? []).map(x => x.ids && x.displayName ? [x.displayName, x.ids] as const : null).filter(x => x != null)
  );


  // parse periods
  const periodMap = new Map<string, string>();
  const periodsFromWeekRanges = new Set<string>();
  if (tables.Period) {
    tables.Period.forEach(period => {
      const id = resolveGuid(period.GUID);
      if (id) periodMap.set(period.Period, id);
    });

    // create additional periods corresponding to the lessons' week/actual weeks
    const _createPeriodsFrom = options.createPeriodsFrom;
    if (_createPeriodsFrom) {
      tables.Lesson?.forEach(x => {
        const key = x[_createPeriodsFrom];
        if (key) periodsFromWeekRanges.add(key);
      });
      periodsFromWeekRanges.forEach(x => periodMap.set(x, generateGUID()));
    }
  }


  // parse locations
  const locations = tables.Room?.map((x): OutTypes.location => ({
    ids:             resolveGuid(x.GUID),
    displayName:     x.Room,
    maximumCapacity: x.SeatingCapacity ? parseInt(x.SeatingCapacity) : undefined,
    ...x.Category?.length && {
      tags: [
        {
          type:  'category',
          value: x.Category
        }
      ]
    }
  }));
  const locationMap = new Map(
    (locations ?? []).map(x => x.ids && x.displayName ? [x.displayName, x.ids] as const : null).filter(x => x != null)
  );


  const subjectMap = new Map(
    tables.Subject?.map(x => [x.Subject, x] as const) ?? []
  );

  // Course can contain FullText and Subject
  const parentCourseMap = new Map(
    tables.Course?.map(x => [x.Course, x] as const) ?? []
  );

  // parse courses
  const courseMap = new Map<string, string | undefined>();
  const courses = (tables.TA ?? []).map((x): OutTypes.course => {
    const ids   = resolveGuid(x.GUID);
    const color = parseColor(subjectMap.get(x.Subject)?.BackgroundColor);

    // Course name is most specific (eg Administration 1), then subject name (Administration), then course code (eg ADMADM01), then subject code (eg ADM)
    const displayName = parentCourseMap.get(x.Course)?.FullText || subjectMap.get(x.Subject)?.FullText || x.Course || x.Subject;

    const teachers = (x.Teacher ?? [])
      .split(',')
      .map(x => teacherMap.get(x.trim()))
      .filter((x): x is NonNullable<typeof x> => !!x)
      .map(to => ({ to }));
    const groups = (x.Group ?? [])
      .split(',')
      .map(x => groupMap.get(x.trim()))
      .filter((x): x is NonNullable<typeof x> => !!x)
      .map(to => ({ to }));

    courseMap.set(`${x.Subject}.${x.Teacher}.${x.Group}`, ids);
    return {
      ids:             ids,
      displayName:     displayName,
      color:           color,
      subject:         x.Subject || parentCourseMap.get(x.Course)?.Subject,
      teachers:        teachers,
      groups:          groups,
      plannedDuration: x.TotalHours ? `${x.TotalHours} hrs` : (x.Weektime ? `${x.Weektime} min/week` : undefined),
      ...x.Period && { period: periodMap.get(x.Period) },
    } satisfies OutTypes.course;
  });


  let lessonMissingCourseWarningsCount = 0;

  const events = tables.Lesson
    ?.filter(x => !isNaN(parseInt(x.Length)))
    .map(x => {

      const position = (() => {
        const dayIndex = daysToIndex[x.DayOfWeek];

        if (dayIndex == null) {
          warnings.push({ code: 'lesson.invalid_day', context: x } satisfies Warning);
          return;
        }

        return {
          start: getDate(dayIndex, x.Starttime),
          end:   getDate(dayIndex, x.Starttime).add(x.Length, 'minutes'),
        };
      })();

      const periodName = options.createPeriodsFrom ? x[options.createPeriodsFrom] : x.Period;
      const periodId   = periodMap.get(periodName);

      const courseKey = `${x.Subject}.${x.Teacher}.${x.Group}`;
      let courseId  = courseMap.get(courseKey);

      const inLocations = (x.Room ?? [])
        .split(',')
        .map(x => locationMap.get(x.trim()))
        .filter((x): x is NonNullable<typeof x> => !!x);

      // if there is a connected course use its display name
      let displayName = courseId
        ? undefined
        : parentCourseMap.get(x.Course)?.FullText || subjectMap.get(x.Subject)?.FullText || x.Course || x.Subject;

      // if there is a connected course use its teachers
      const teachers = courseId
        ? undefined
        : x.Teacher
          ?.split(',')
          .map(x => teacherMap.get(x.trim()))
          .filter((x): x is NonNullable<typeof x> => !!x)
          .map(to => ({ to }));

      // if there is a connected course use its groups
      const groups = courseId
        ? undefined
        : x.Group
          ?.split(',')
          .map(x => groupMap.get(x.trim()))
          .filter((x): x is NonNullable<typeof x> => !!x)
          .map(to => ({ to }));

      const duration = x.Length ? parseInt(x.Length) : undefined;

      // If no courseId found, we handle it according to options.includeEventsMissingCourse
      if (!courseId && options.includeEventsMissingCourse) {
        // Create a new course for this event
        courseId = generateGUID();
        const subject = subjectMap.get(x.Subject);
        const subjectName = subject?.FullText || x.Subject;
        displayName = displayName || subject?.FullText || x.Subject;
        const newCourse: OutTypes.course = {
          ids:         courseId,
          displayName: displayName,
          subject:     subjectName,
          teachers:    teachers,
          groups:      groups,
        };
        courses?.push(newCourse);
      } else if (!courseId) {
        lessonMissingCourseWarningsCount += 1;
        return null; // will be filtered out later
      }

      return {
        ids:               resolveGuid(x.GUID),
        displayName:       displayName,
        ...position,
        duration:          duration,
        preferredDuration: duration ?? 0,
        course:            courseId,
        period:            periodId,
        inLocations,
        ...(!options.includeEventsMissingCourse ? { teachers, groups } : undefined),
      } satisfies OutTypes.event;
    }).filter((x): x is NonNullable<typeof x> => x != null);

  const periods = tables.Period
    ?.map(x => parsePeriod(x, scheduleStart, options, warnings))
    .filter(x => x.ranges.length > 0);

  periodsFromWeekRanges.forEach(x => {
    const ids = periodMap.get(x);
    if (!ids) {
      throw new Error(`Unable to find period for ${x}`);
      return;
    }
    periods?.push(fromWeekNumberRange(ids, x, scheduleStart));
  });

  if (lessonMissingCourseWarningsCount > 0 && !options.includeEventsMissingCourse) {
    warnings.unshift({
      code:    'ignoring_lessons_missing_course',
      context: { count: lessonMissingCourseWarningsCount }
    } satisfies Warning);
  }

  return {
    settings,
    periods:   periods   ?? [],
    teachers:  teachers  ?? [],
    persons:   (students  ?? []).concat(teacherPersons ?? []),
    groups:    groups    ?? [],
    locations: locations ?? [],
    courses:   courses   ?? [],
    events:    events    ?? [],

    meta: { warnings }
  } satisfies Out;
};